import React from "react";

import { Checkbox, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import { useAccountSelector, useDateFormatSelect } from "../../../store";

type Props = {
  from: string | null;
  to: string | null;
  setFrom: (value: string | null) => void;
  setTo: (value: string | null) => void;
  fromInception: boolean;
  setFromInception: (value: boolean) => void;
  useAccountingDate: boolean;
  setUseAccountingDate: (value: boolean) => void;
  fromLabel?: string;
  toLabel?: string;
  /** Hides the "From Inception" checkbox and the Effective/Accounting date switch (e.g. for the pipeline). */
  hideExtraOptions?: boolean;
};

export const ManualDateRange = ({
  from,
  setFrom,
  to,
  setTo,
  fromInception,
  setFromInception,
  fromLabel,
  toLabel,
  useAccountingDate,
  setUseAccountingDate,
  hideExtraOptions
}: Props) => {
  const dateFormat = useDateFormatSelect();
  const isRangeSwitchEnabled = useAccountSelector()?.allow_use_date_range_switch || false;

  return (
    <Stack gap="10px">
      <Stack paddingTop="6px" direction="row" justifyContent="space-between" alignItems={"center"} gap={"14px"}>
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          maxDate={to ? dayjs(to) : undefined}
          slotProps={{
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            },
            textField: {
              sx: { width: "180px", fontSize: "16px" }
            }
          }}
          value={from ? dayjs(from) : null}
          label={fromLabel || "From"}
          disabled={fromInception}
          onChange={(value) => {
            if (value) {
              setFrom(value.format("YYYY-MM-DD"));
            }
          }}
        />
        <Divider orientation="horizontal" sx={{ width: "13px" }} />
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          minDate={from ? dayjs(from) : undefined}
          slotProps={{
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            },
            textField: {
              sx: { width: "180px", fontSize: "16px" }
            }
          }}
          value={to ? dayjs(to) : null}
          label={toLabel || "To"}
          onChange={(value) => {
            if (value) {
              setTo(value.format("YYYY-MM-DD"));
            }
          }}
        />
      </Stack>
      {!hideExtraOptions && (
        <>
          <FormControlLabel
            control={<Checkbox checked={fromInception} onChange={(e) => setFromInception(e.target.checked)} />}
            label="From Inception"
          />
          {isRangeSwitchEnabled && (
            <>
              <Divider sx={{ borderColor: "#DBE8F5" }} />
              <FormControl>
                <RadioGroup
                  row
                  value={useAccountingDate}
                  onChange={(e) => setUseAccountingDate(e.target.value === "true")}
                >
                  <FormControlLabel value={false} control={<Radio />} label="Effective Date" />
                  <FormControlLabel value={true} control={<Radio />} label="Accounting Date" />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </>
      )}
    </Stack>
  );
};
